import React, { useState } from "react";
import JSZip from "jszip";
import { Download } from "lucide-react";
import { axiosPrivate } from "../../api/axios";
import employeeRequestService from "../../api/employeeRequestService";
import { formatSize } from "./constants";
import { extOf, kindOf } from "./fileKinds";
import { tint } from "./format";
import AuthImage from "./AuthImage";
import AttachmentLightbox from "./AttachmentLightbox";

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/** Attachment tiles + "Download all" (zipped) + preview lightbox. */
const AttachmentsSection = ({ attachments, requestRef }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [zipping, setZipping] = useState(false);

  const downloadAll = async () => {
    if (attachments.length === 1) {
      return employeeRequestService.downloadAttachment(attachments[0].url, attachments[0].name).catch(() => {});
    }
    setZipping(true);
    try {
      const zip = new JSZip();
      const used = new Set();
      for (const a of attachments) {
        const { data } = await axiosPrivate.get(a.url, { responseType: "blob" });
        let name = a.name;
        for (let i = 2; used.has(name); i += 1) name = a.name.replace(/(\.[^.]*)?$/, ` (${i})$1`);
        used.add(name);
        zip.file(name, data);
      }
      saveBlob(await zip.generateAsync({ type: "blob" }), `${requestRef}-attachments.zip`);
    } catch {
      /* a failed file simply aborts the zip; individual downloads still work */
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="er-section">
      <div className="er-row" style={{ justifyContent: "space-between", gap: 8 }}>
        <div className="er-label">Attachments · {attachments.length}</div>
        <button
          type="button"
          className="er-btn"
          onClick={downloadAll}
          disabled={zipping}
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, color: "var(--accent-ink)", padding: "4px 6px", borderRadius: 7, whiteSpace: "nowrap" }}
        >
          <Download size={13} strokeWidth={2} /> {zipping ? "Preparing…" : "Download all"}
        </button>
      </div>
      <div className="er-atts">
        {attachments.map((a, i) => {
          const kind = kindOf(a.name);
          const Icon = kind.icon;
          return (
            <button key={a.id} type="button" className="er-btn er-att" onClick={() => setOpenIndex(i)} title={a.name}>
              <div className="er-attTop" style={{ background: `radial-gradient(120px 80px at 50% 40%, ${tint(kind.color, 16)}, transparent 75%)` }}>
                {a.isImage ? (
                  <AuthImage url={a.url} alt={a.name} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: tint(kind.color, 16), display: "grid", placeItems: "center" }}>
                    <Icon size={22} strokeWidth={1.8} color={kind.color} />
                  </div>
                )}
                <span className="er-ext" style={{ color: kind.color }}>{extOf(a.name) || "file"}</span>
              </div>
              <div className="er-attBottom">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="er-ellipsis" style={{ fontSize: 12.5, fontWeight: 500 }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: "var(--faint)" }}>{kind.kind} · {formatSize(a.size)}</div>
                </div>
                <span title="Download" style={{ width: 26, height: 26, borderRadius: 8, display: "grid", placeItems: "center", color: "var(--faint)", flexShrink: 0 }}>
                  <Download size={14} />
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {openIndex !== null && (
        <AttachmentLightbox files={attachments} index={openIndex} onIndex={setOpenIndex} onClose={() => setOpenIndex(null)} />
      )}
    </div>
  );
};

export default AttachmentsSection;
